import * as React from "react";
import { useState } from "react";
import { DeleteModal } from "../../../common/delete-modal";
import { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";

/**
 * The Delete a switcher row offers, behind the same confirmation the datasources list uses.
 *
 * The confirmation is rendered by the chip, BESIDE the switcher's popover rather than inside it:
 * picking a menu item closes the popover, and anything inside it unmounts with it. The chip
 * itself stays mounted — it is the bar's name — so the dialog outlives the pick.
 */

interface INamed {
  name?: string;
}

export interface ISwitcherDelete<T> {
  /** The "…" item: opens the confirmation for `target`. */
  deleteOption: (target: T, hidden?: boolean) => KuikaDropdownMenuItemType;
  /** Opens the confirmation directly — for a host whose row actions come from a shared builder. */
  requestDelete: (target: T) => void;
  /** Render next to the switcher. */
  deleteModal: React.ReactNode;
}

export const useSwitcherDelete = <T extends INamed>(
  deleteType: string,
  onConfirm: (target: T) => void
): ISwitcherDelete<T> => {
  const [target, setTarget] = useState<T | undefined>(undefined);

  return {
    requestDelete: setTarget,
    deleteOption: (row: T, hidden?: boolean): KuikaDropdownMenuItemType => ({
      key: "delete",
      label: "Delete",
      icon: <KuikaSvg name="trash" width={16} height={16} />,
      danger: true,
      hidden,
      onClick: (): void => setTarget(row)
    }),
    deleteModal: (
      <DeleteModal
        itemName={target?.name ?? ""}
        triggerModal={target !== undefined}
        deleteType={deleteType}
        deleteItem={(): void => {
          if (target) onConfirm(target);
        }}
        closeModal={(): void => setTarget(undefined)}
      />
    )
  };
};
